import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAllTags, isNotForSale } from '../utils/tagUtils';
import { API_BASE_URL } from '../config/api';

// Showcase page: lists the designs in a category as wide "cards" in a
// 2-column layout (image left, description + price right), rendered in a
// chosen print format (e.g. /showcase/Card_A2/dogs).
function ShowcasePage() {
  const { format: formatParam, category } = useParams();
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [format, setFormat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [designsRes, formatsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/designs`),
          axios.get(`${API_BASE_URL}/formats`)
        ]);

        if (cancelled) return;

        if (designsRes.data.success && formatsRes.data.success) {
          setDesigns(designsRes.data.data || []);
          const match = (formatsRes.data.data || []).find(
            (f) => f.id.toLowerCase() === (formatParam || '').toLowerCase()
          );
          if (match) {
            setFormat(match);
          } else {
            setError(`Unknown format "${formatParam}"`);
          }
        } else {
          setError('Failed to load showcase data');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load showcase');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [formatParam]);

  const shownDesigns = useMemo(() => {
    if (!category) return designs;
    const wanted = category.toLowerCase();
    return designs.filter((design) =>
      getAllTags(design.tags).some((tag) => tag && tag.toLowerCase() === wanted)
    );
  }, [designs, category]);

  const previewUrl = (designId) =>
    `${API_BASE_URL}/preview?designId=${encodeURIComponent(designId)}&formatId=${encodeURIComponent(
      format.id
    )}&mockupIndex=0`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading showcase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Showcase unavailable</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
            >
              Back to gallery
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 lg:py-12 px-4 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <header className="mb-6 md:mb-10">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            {format.name}
          </h1>
          <p className="mt-1 text-sm md:text-base text-gray-600">
            {category ? (
              <>
                Category: <span className="font-medium text-gray-800">{category}</span> ·{' '}
              </>
            ) : null}
            {shownDesigns.length} design{shownDesigns.length !== 1 ? 's' : ''}
          </p>
        </header>

        {shownDesigns.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No designs found for this selection.</p>
          </div>
        ) : (
          <div className="space-y-6 md:space-y-8">
            {shownDesigns.map((design) => {
              const notForSale = isNotForSale(design);
              return (
                <button
                  key={design.id}
                  type="button"
                  onClick={() => navigate(`/design/${design.id}?format=${encodeURIComponent(format.id)}`)}
                  className="w-full text-left bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden border border-gray-100"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    {/* Image column */}
                    <div className="relative bg-gray-100 aspect-square overflow-hidden">
                      <img
                        src={previewUrl(design.id)}
                        alt={design.title}
                        className="w-full h-full object-contain"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `${API_BASE_URL}/designs/${design.id}/image`;
                        }}
                      />
                      {notForSale && (
                        <div className="absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium bg-amber-500 text-white border border-amber-600 shadow-md">
                          Display Only
                        </div>
                      )}
                    </div>

                    {/* Text column */}
                    <div className="p-5 md:p-7 flex flex-col">
                      <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                        {design.title}
                      </h2>

                      {design.description && (
                        <div
                          className="text-sm text-gray-600 mb-4 line-clamp-4 [&_a]:underline [&_a]:text-blue-600 [&_strong]:font-semibold [&_em]:italic"
                          dangerouslySetInnerHTML={{ __html: design.description }}
                        />
                      )}

                      <div className="mt-auto pt-4 flex items-center justify-between border-t border-gray-100">
                        <span className="text-xl md:text-2xl font-bold text-gray-900">
                          ${format.price}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-600">
                          Order
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ShowcasePage;
